import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { APIHOST } from '../../common';
import { getToken } from '../../components/auth/AuthContext';

interface ContentPage {
  id: number;
  documentId: string;
  key: string;
  content: string;
}

export default function AdminContentPage() {
  const [pages, setPages] = useState<ContentPage[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [selectedPage, setSelectedPage] = useState<ContentPage | null>(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchPages = async () => {
      setLoading(true);
      setError('');
      try {
        const token = getToken();
        const response = await fetch(`${APIHOST}/api/contents`, {
          
        });
        if (!response.ok) throw new Error('Failed to load content pages');
        const data = await response.json();
        setPages(data.data || []);
      } catch (err) {
        setError('Could not load content pages.');
      } finally {
        setLoading(false);
      }
    };
    fetchPages();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSelectedPage(null);
      setContent('');
      return;
    }
    const page = pages.find(p => p.documentId === selectedId);
    setSelectedPage(page || null);
    setContent(page?.content || '');
    setSuccess('');
    setError('');
  }, [selectedId, pages]);

  const handleSave = async () => {
    if (!selectedPage) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const token = getToken();
      const response = await fetch(`${APIHOST}/api/contents/${selectedPage.documentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ data: { content } })
      });
      if (!response.ok) throw new Error('Failed to save content');
      setSuccess('Content saved successfully.');
    } catch (err) {
      setError('Failed to save content.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" gutterBottom>
        Content Page Editor
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="content-page-select-label">Select Content Page</InputLabel>
        <Select
          labelId="content-page-select-label"
          value={selectedId}
          label="Select Content Page"
          onChange={e => setSelectedId(e.target.value)}
          disabled={loading || pages.length === 0}
        >
          {pages.map(page => (
            <MenuItem key={page.documentId} value={page.documentId}>
              {page.key}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedPage && (
        <Box>
          <TextField
            label="Content"
            value={content}
            onChange={e => setContent(e.target.value)}
            multiline
            minRows={10}
            fullWidth
            variant="outlined"
            sx={{ mb: 3, fontFamily: 'monospace' }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      )}
      {loading && <CircularProgress sx={{ mt: 4 }} />}
    </Container>
  );
}
